/**
 * LiveTranslationProvider — navigator-root context for the Live Translation
 * feature. Mounted in app/_layout.tsx so a session survives navigation
 * between visit sections; without this, every screen transition would tear
 * down the WebRTC peer connection.
 *
 * Exposes:
 *   - useLiveTranslation()      — hook used by buttons + the recording pill
 *   - <LiveTranslationModal />  — the modal itself (rendered by the provider)
 */
import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode
} from "react";

import { useActiveLiveTranslationSession } from "./useActiveLiveTranslationSession";

import { LiveTranslationModal } from "@/components/visit/LiveTranslationModal";

interface OpenInput {
    visitId: string;
    preselectedLanguage?: string | null;
}

interface LiveTranslationContextValue {
    /** True when the modal is currently visible. */
    isOpen: boolean;
    /** True when the session is active (modal may be closed but session running). */
    isActive: boolean;
    activeVisitId: string | null;
    targetLanguage: string | null;
    durationMs: number;
    /** Set during the WebRTC handshake so the pill can show "Connecting…"
     *  instead of a 00:00 timer. Null once the session is fully live. */
    connectingHint: string | null;
    /** Returns the language remembered for a visit (set on first successful
     *  start in that visit). Null if no language has been chosen yet for it. */
    rememberedLanguage: (visitId: string) => string | null;
    open: (input: OpenInput) => void;
    closeModal: () => void;
    /** Tear down current session and immediately start a fresh one with the
     *  language we remember for this visit. */
    restartSession: () => Promise<void>;
    /** Stop the current session and re-open the picker so the nurse can
     *  pick a different target language without leaving the visit. */
    changeLanguage: () => Promise<void>;
    stopSession: () => Promise<void>;
}

const LiveTranslationContext = createContext<LiveTranslationContextValue | null>(null);

export function useLiveTranslation(): LiveTranslationContextValue {
    const ctx = useContext(LiveTranslationContext);
    if (!ctx) {
        throw new Error("useLiveTranslation must be used within a LiveTranslationProvider");
    }
    return ctx;
}

export function LiveTranslationProvider({ children }: { children: ReactNode }) {
    const session = useActiveLiveTranslationSession();
    const [isOpen, setIsOpen] = useState(false);
    const [openInput, setOpenInput] = useState<OpenInput | null>(null);
    // Per-visit memory of the last language picked. Once the nurse has chosen
    // a language for visit X, subsequent entry-point taps + pill restarts
    // skip the picker entirely for that visit.
    const [lastLanguageByVisit, setLastLanguageByVisit] = useState<Record<string, string>>({});
    // Tracks the last status we acted on so we only auto-close once per
    // transition into "connecting" (not on every status update during the
    // session).
    const lastClosedForStatus = useRef<string | null>(null);

    const isActive = session.status === "session-active" || session.status === "connecting";

    // Auto-close the modal as soon as the session starts connecting — the
    // nurse goes back to using the visit form and the recording pill becomes
    // the only Live Translation UI. Re-open automatically on error so the
    // failure isn't silent.
    useEffect(() => {
        if (
            (session.status === "connecting" || session.status === "session-active") &&
            isOpen &&
            lastClosedForStatus.current !== "connecting"
        ) {
            lastClosedForStatus.current = "connecting";
            setIsOpen(false);
        }
        if (session.status === "error" && !isOpen) {
            lastClosedForStatus.current = null;
            setIsOpen(true);
        }
        if (session.status === "idle") {
            lastClosedForStatus.current = null;
        }
    }, [session.status, isOpen]);

    // Capture the chosen language into per-visit memory once the session
    // actually connects (not just on start) — that way a failed mint doesn't
    // poison future restarts with a never-worked language.
    useEffect(() => {
        if (session.status === "session-active" && openInput?.visitId && session.targetLanguage) {
            setLastLanguageByVisit((prev) =>
                prev[openInput.visitId] === session.targetLanguage
                    ? prev
                    : { ...prev, [openInput.visitId]: session.targetLanguage as string }
            );
        }
    }, [session.status, openInput?.visitId, session.targetLanguage]);

    const value = useMemo<LiveTranslationContextValue>(
        () => ({
            isOpen,
            isActive,
            activeVisitId: openInput?.visitId ?? null,
            targetLanguage: session.targetLanguage,
            durationMs: session.durationMs,
            connectingHint: session.status === "connecting" ? "Connecting…" : null,
            rememberedLanguage: (visitId) => lastLanguageByVisit[visitId] ?? null,
            open: (input) => {
                setOpenInput(input);
                const remembered =
                    input.preselectedLanguage ?? lastLanguageByVisit[input.visitId] ?? null;
                if (remembered) {
                    // Visit already has a chosen language — skip the picker
                    // and start the session immediately. Pill takes over.
                    void session.start({
                        visitId: input.visitId,
                        targetLanguage: remembered
                    });
                    return;
                }
                setIsOpen(true);
            },
            closeModal: () => {
                setIsOpen(false);
            },
            restartSession: async () => {
                const visitId = openInput?.visitId ?? null;
                if (!visitId) {
                    return;
                }
                const lang = lastLanguageByVisit[visitId] ?? session.targetLanguage ?? null;
                if (!lang) {
                    return;
                }
                await session.stop();
                session.reset();
                await session.start({ visitId, targetLanguage: lang });
            },
            changeLanguage: async () => {
                const visitId = openInput?.visitId ?? null;
                if (!visitId) {
                    return;
                }
                await session.stop();
                session.reset();
                // Drop the remembered language for this visit so the modal
                // shows the picker instead of auto-starting. Reset the
                // auto-close tracker too so the next start cleanly closes
                // the modal.
                setLastLanguageByVisit((prev) => {
                    if (!(visitId in prev)) {
                        return prev;
                    }
                    const next: Record<string, string> = {};
                    for (const key of Object.keys(prev)) {
                        if (key !== visitId) {
                            next[key] = prev[key];
                        }
                    }
                    return next;
                });
                lastClosedForStatus.current = null;
                setOpenInput({ visitId });
                setIsOpen(true);
            },
            stopSession: async () => {
                await session.stop();
                session.reset();
                setIsOpen(false);
                // Intentionally keep openInput + lastLanguageByVisit so the
                // next entry-point tap in the same visit goes straight to
                // start (no language picker), per UX intent.
            }
        }),
        [isOpen, isActive, openInput, session, lastLanguageByVisit]
    );

    return (
        <LiveTranslationContext.Provider value={value}>
            {children}
            <LiveTranslationModal
                visible={isOpen}
                visitId={openInput?.visitId ?? null}
                preselectedLanguage={openInput?.preselectedLanguage ?? null}
                session={session}
                onClose={() => setIsOpen(false)}
            />
        </LiveTranslationContext.Provider>
    );
}
